import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import * as moment from 'moment';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { AdminService } from '../../../services/admin.service';
import { GroupResponse } from '../../../services/admin.service.types';
import { UserService } from '../../../services/user.service';
import { AppConstants } from '../../views/view-user-profile/app.constants';
import { VacationApiService } from './vacation-api.service';
import {
  VACATION_ENTRY_STATUS_TRANSLATION,
  VACATION_TYPE_TRANSLATION
} from './vacation-booking-panel.constants';
import {
  VACATION_TEAM_CALENDAR_CONFIG,
  VACATION_TEAM_CALENDAR_KEY,
  VACATION_TEAM_CALENDAR_MONTH_KEYS,
  VACATION_TEAM_CALENDAR_MONTH_UNIT,
  VACATION_TEAM_CALENDAR_PLACEHOLDER_PREFIX,
  VACATION_TEAM_CALENDAR_WEEKDAY_KEYS
} from './vacation-team-calendar.constants';
import {
  VacationGroupCalendarDay,
  VacationGroupCalendarEntry,
  VacationGroupCalendarResponse,
  VacationTeamCalendarCell
} from './vacation.models';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const DEPARTMENT_GROUP = 'DEPARTMENT';

@Component({
  selector: 'app-vacation-team-calendar',
  templateUrl: './vacation-team-calendar.component.html',
  styleUrls: ['./vacation-team-calendar.component.css']
})
export class VacationTeamCalendarComponent implements OnInit, OnDestroy {

  readonly config = VACATION_TEAM_CALENDAR_CONFIG;
  readonly statusTranslation = VACATION_ENTRY_STATUS_TRANSLATION;
  readonly typeTranslation = VACATION_TYPE_TRANSLATION;
  readonly weekdayKeys = VACATION_TEAM_CALENDAR_WEEKDAY_KEYS;

  groups: GroupResponse[] = [];
  calendar: VacationGroupCalendarResponse;
  calendarCells: VacationTeamCalendarCell[] = [];
  selectedDay: VacationGroupCalendarDay;
  selectedGroupId = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
  selectedDate = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
  monthKey = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
  monthYear: number;
  errorKey = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
  isVisible = false;
  isAdmin = false;
  groupsLoading = false;
  calendarLoading = false;
  private monthCursor = moment().startOf(VACATION_TEAM_CALENDAR_MONTH_UNIT);
  private destroy$ = new Subject<void>();
  private calendarChanged$ = new Subject<void>();

  constructor(
    private vacationApiService: VacationApiService,
    private adminService: AdminService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.updateMonthContext();
    this.configureScope();
  }

  ngOnDestroy(): void {
    this.calendarChanged$.next();
    this.calendarChanged$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  previousMonth(): void {
    this.changeMonth(VACATION_TEAM_CALENDAR_CONFIG.PREVIOUS_MONTH_STEP);
  }

  nextMonth(): void {
    this.changeMonth(VACATION_TEAM_CALENDAR_CONFIG.NEXT_MONTH_STEP);
  }

  selectGroup(groupId: string): void {
    if (!groupId || groupId === this.selectedGroupId || !this.groups.some(group => group.id === groupId)) {
      return;
    }
    this.selectedGroupId = groupId;
    this.loadCalendar();
  }

  retry(): void {
    if (this.selectedGroupId) {
      this.loadCalendar();
      return;
    }
    const user = this.userService.getLocal();
    if (this.isVisible && user) {
      this.loadGroupOptions(user.mainGroup);
    }
  }

  selectDay(day: VacationGroupCalendarDay): void {
    if (this.selectedDate === day.date) {
      this.selectedDate = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
      this.selectedDay = undefined;
      return;
    }
    this.selectedDate = day.date;
    this.selectedDay = day;
  }

  trackByGroupId(index: number, group: GroupResponse): string {
    return group.id;
  }

  trackByCellKey(index: number, cell: VacationTeamCalendarCell): string {
    return cell.key;
  }

  trackByTranslationKey(index: number, key: string): string {
    return key;
  }

  trackByEntryUser(index: number, entry: VacationGroupCalendarEntry): string {
    return entry.userId;
  }

  private configureScope(): void {
    const user = this.userService.getLocal();
    if (!user || !user.roles) {
      return;
    }
    this.isAdmin = user.roles.indexOf(AppConstants.ROLES.ADMIN)
      !== VACATION_TEAM_CALENDAR_CONFIG.ROLE_NOT_FOUND_INDEX;
    const isHead = user.roles.indexOf(AppConstants.ROLES.HEAD)
      !== VACATION_TEAM_CALENDAR_CONFIG.ROLE_NOT_FOUND_INDEX;
    this.isVisible = this.isAdmin || isHead;
    if (!this.isVisible) {
      return;
    }
    this.loadGroupOptions(user.mainGroup);
  }

  private loadGroupOptions(preferredGroupId: string): void {
    this.groupsLoading = true;
    this.groups = [];
    this.selectedGroupId = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
    this.errorKey = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
    const request: Observable<HttpResponse<Object>> = this.isAdmin
      ? this.adminService.getAdminGroupOptions() : this.adminService.getGroupsAndPositionsHead();
    request
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.acceptGroups((response.body || []) as GroupResponse[], preferredGroupId),
        () => {
          this.groupsLoading = false;
          this.errorKey = VACATION_TEAM_CALENDAR_KEY.GROUPS_LOAD_ERROR;
        }
      );
  }

  private acceptGroups(groups: GroupResponse[], preferredGroupId: string): void {
    if (!Array.isArray(groups)) {
      this.groupsLoading = false;
      this.errorKey = VACATION_TEAM_CALENDAR_KEY.GROUPS_LOAD_ERROR;
      return;
    }
    this.groups = this.calendarGroups(groups);
    this.groupsLoading = false;
    this.selectedGroupId = this.resolveGroupId(preferredGroupId);
    if (!this.selectedGroupId) {
      this.errorKey = VACATION_TEAM_CALENDAR_KEY.NO_GROUPS;
      return;
    }
    this.loadCalendar();
  }

  private calendarGroups(groups: GroupResponse[]): GroupResponse[] {
    return groups.filter(group => !group.groupType || group.groupType === DEPARTMENT_GROUP)
      .filter(group => !group.parentDepartmentId
        || (!this.isAdmin && !groups.some(parent => parent.id === group.parentDepartmentId)));
  }

  private resolveGroupId(preferredGroupId: string): string {
    const preferredExists = this.groups.some(group => group.id === preferredGroupId);
    return preferredExists
      ? preferredGroupId
      : (this.groups.length
        ? this.groups[VACATION_TEAM_CALENDAR_CONFIG.FIRST_INDEX].id
        : VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT);
  }

  private loadCalendar(): void {
    if (!this.selectedGroupId) {
      return;
    }
    const from = this.monthCursor.clone().startOf(VACATION_TEAM_CALENDAR_MONTH_UNIT)
      .format(VACATION_TEAM_CALENDAR_CONFIG.DATE_FORMAT);
    const to = this.monthCursor.clone().endOf(VACATION_TEAM_CALENDAR_MONTH_UNIT)
      .format(VACATION_TEAM_CALENDAR_CONFIG.DATE_FORMAT);
    this.startCalendarLoad();
    this.vacationApiService.getGroupCalendar(this.selectedGroupId, from, to)
      .takeUntil(this.calendarChanged$).takeUntil(this.destroy$)
      .subscribe(
        response => this.acceptCalendar(response),
        error => this.failCalendar(error)
      );
  }

  private acceptCalendar(response: VacationGroupCalendarResponse): void {
    if (!response || response.groupId !== this.selectedGroupId || !Array.isArray(response.days)) {
      this.failCalendar(null);
      return;
    }
    this.calendar = response;
    this.calendarCells = this.buildCalendarCells(response.days);
    this.calendarLoading = false;
  }

  private failCalendar(error: HttpErrorResponse): void {
    this.calendarLoading = false;
    this.errorKey = VACATION_TEAM_CALENDAR_KEY.CALENDAR_LOAD_ERROR;
    if (error && (error.status === HTTP_FORBIDDEN || error.status === HTTP_UNAUTHORIZED)) {
      this.groups = [];
      this.selectedGroupId = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
    }
  }

  private changeMonth(step: number): void {
    if (this.calendarLoading || this.groupsLoading) {
      return;
    }
    this.monthCursor.add(step, VACATION_TEAM_CALENDAR_MONTH_UNIT);
    this.updateMonthContext();
    this.loadCalendar();
  }

  private updateMonthContext(): void {
    this.monthKey = VACATION_TEAM_CALENDAR_MONTH_KEYS[this.monthCursor.month()];
    this.monthYear = this.monthCursor.year();
  }

  private startCalendarLoad(): void {
    this.calendarChanged$.next();
    this.calendar = undefined;
    this.calendarCells = [];
    this.selectedDate = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
    this.selectedDay = undefined;
    this.calendarLoading = true;
    this.errorKey = VACATION_TEAM_CALENDAR_CONFIG.EMPTY_TEXT;
  }

  private buildCalendarCells(days: VacationGroupCalendarDay[]): VacationTeamCalendarCell[] {
    const cells: VacationTeamCalendarCell[] = [];
    const orderedDays = days.slice()
      .sort((left, right) => left.date.localeCompare(right.date));
    const placeholderCount = this.monthCursor.clone().startOf(VACATION_TEAM_CALENDAR_MONTH_UNIT)
      .isoWeekday() - VACATION_TEAM_CALENDAR_CONFIG.FIRST_ISO_WEEKDAY;
    for (let index = VACATION_TEAM_CALENDAR_CONFIG.FIRST_INDEX; index < placeholderCount; index++) {
      cells.push({
        key: VACATION_TEAM_CALENDAR_PLACEHOLDER_PREFIX + index,
        placeholder: true
      });
    }
    orderedDays.forEach(day => cells.push({
      key: day.date,
      placeholder: false,
      day: day,
      dayNumber: moment(day.date).date()
    }));
    return cells;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/vacation/vacation-team-calendar.component.ts